import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp, CardTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { formSnapshotDirty } from "@/lib/formDirty";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

interface LinkSource {
  code: string;
  label: string;
  trainingSiteOrigin: string;
  fallbackPath: string;
  isActive: boolean;
}

interface LinkDestination {
  id: number;
  sourceCode: string;
  matchKind: "exact_sku" | "sku_prefix";
  matchValue: string;
  destinationPath: string;
  priority: number;
  endUserFsAccountId: number | null;
  notes: string | null;
  isActive: boolean;
}

interface NavEvent {
  id: number;
  scannedAt: string;
  sourceCode: string;
  shipmentId: number | null;
  batchItemId: number | null;
  sku: string | null;
  endUserName: string | null;
  resolvedUrl: string;
  resolution: string;
}

interface DestinationDraft {
  sourceCode: string;
  matchKind: "exact_sku" | "sku_prefix";
  matchValue: string;
  destinationPath: string;
  priority: string;
  endUserFsAccountId: string;
  notes: string;
}

const EMPTY_DESTINATION_DRAFT: DestinationDraft = {
  sourceCode: "training",
  matchKind: "exact_sku",
  matchValue: "",
  destinationPath: "/training/",
  priority: "0",
  endUserFsAccountId: "",
  notes: "",
};

export function ProductScanLinksPage() {
  const queryClient = useQueryClient();
  const canEdit = useHubModuleAdmin("admin");
  const [selectedSource, setSelectedSource] = useState("training");
  const [destinationDraft, setDestinationDraft] = useState<DestinationDraft>(EMPTY_DESTINATION_DRAFT);
  const [previewShipmentId, setPreviewShipmentId] = useState("");
  const [previewBatchItemId, setPreviewBatchItemId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const sourcesQuery = useQuery({
    queryKey: ["admin", "product-scan-links", "sources"],
    queryFn: () =>
      apiFetch<{ sources: LinkSource[]; linkOrigin: string }>(
        "/api/user-admin/product-scan-links/sources",
      ),
  });

  const destinationsQuery = useQuery({
    queryKey: ["admin", "product-scan-links", "destinations", selectedSource],
    queryFn: () =>
      apiFetch<{ destinations: LinkDestination[] }>(
        `/api/user-admin/product-scan-links/destinations?source=${encodeURIComponent(selectedSource)}`,
      ),
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "product-scan-links", "stats", selectedSource],
    queryFn: () =>
      apiFetch<{ stats: { total: number; matched: number; fallback: number; invalid: number } }>(
        `/api/user-admin/product-scan-links/stats?source=${encodeURIComponent(selectedSource)}`,
      ),
  });

  const eventsQuery = useQuery({
    queryKey: ["admin", "product-scan-links", "events", selectedSource],
    queryFn: () =>
      apiFetch<{ events: NavEvent[] }>(
        `/api/user-admin/product-scan-links/nav-events?source=${encodeURIComponent(selectedSource)}&limit=50`,
      ),
  });

  const previewQuery = useQuery({
    queryKey: [
      "admin",
      "product-scan-links",
      "preview",
      selectedSource,
      previewShipmentId,
      previewBatchItemId,
    ],
    enabled:
      previewShipmentId.trim() !== "" &&
      previewBatchItemId.trim() !== "" &&
      Number(previewShipmentId) > 0 &&
      Number(previewBatchItemId) > 0,
    queryFn: () =>
      apiFetch<{
        scanUrl: string;
        resolvedUrl: string;
        resolution: string;
        context: {
          sku: string;
          endUserName: string | null;
          salesOrderNumber: string | null;
        } | null;
      }>(
        `/api/user-admin/product-scan-links/preview?so=${encodeURIComponent(selectedSource)}&si=${encodeURIComponent(
          previewShipmentId.trim(),
        )}&bii=${encodeURIComponent(previewBatchItemId.trim())}`,
      ),
  });

  const createDestinationMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ destination: LinkDestination }>("/api/user-admin/product-scan-links/destinations", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setFormError(null);
      setDestinationDraft({ ...EMPTY_DESTINATION_DRAFT, sourceCode: selectedSource });
      void queryClient.invalidateQueries({ queryKey: ["admin", "product-scan-links", "destinations"] });
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : "Could not save destination.");
    },
  });

  const deleteDestinationMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/user-admin/product-scan-links/destinations/${String(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "product-scan-links", "destinations"] });
    },
  });

  const linkOrigin = sourcesQuery.data?.linkOrigin ?? "https://link.smartgridsolutions.com";
  const sources = sourcesQuery.data?.sources ?? [];
  const destinations = destinationsQuery.data?.destinations ?? [];
  const stats = statsQuery.data?.stats;

  const destinationDirty = useMemo(
    () =>
      formSnapshotDirty(destinationDraft, {
        ...EMPTY_DESTINATION_DRAFT,
        sourceCode: selectedSource,
      }),
    [destinationDraft, selectedSource],
  );

  return (
    <div className="space-y-6">
      <PageTitleWithHelp
        hoverHint={
          "QR and data matrix codes on product labels open link.smartgridsolutions.com with shipment and batch line context. " +
          "This page maps SKUs to training pages on smartgridsolutions.com and logs every scan for adoption tracking."
        }
      >
        Product scan links
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Link origin</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2 text-sm text-slate-700">
          <p>
            Printed URL host: <span className="font-mono text-slate-900">{linkOrigin}</span>
          </p>
          <p className="text-slate-600">
            Example query:{" "}
            <span className="font-mono text-slate-800">?so=training&amp;si=2055&amp;bii=49840</span>
          </p>
        </CardBody>
      </Card>

      {stats != null && (
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">Total scans</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">Matched</p>
              <p className="text-2xl font-semibold text-emerald-700">{stats.matched}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">Fallback</p>
              <p className="text-2xl font-semibold text-amber-700">{stats.fallback}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">Invalid</p>
              <p className="text-2xl font-semibold text-rose-700">{stats.invalid}</p>
            </CardBody>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>URL preview</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="preview-source">Source</Label>
              <select
                id="preview-source"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={selectedSource}
                onChange={(event) => {
                  setSelectedSource(event.target.value);
                  setDestinationDraft((draft) => ({ ...draft, sourceCode: event.target.value }));
                }}
              >
                {sources.map((source) => (
                  <option key={source.code} value={source.code}>
                    {source.label} ({source.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="preview-shipment-id">Shipment id (si)</Label>
              <Input
                id="preview-shipment-id"
                value={previewShipmentId}
                onChange={(event) => setPreviewShipmentId(event.target.value)}
                placeholder="2055"
              />
            </div>
            <div>
              <Label htmlFor="preview-batch-item-id">Batch item id (bii)</Label>
              <Input
                id="preview-batch-item-id"
                value={previewBatchItemId}
                onChange={(event) => setPreviewBatchItemId(event.target.value)}
                placeholder="49840"
              />
            </div>
          </div>
          {previewQuery.data != null && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
              <p>
                <span className="font-medium text-slate-700">Scan URL:</span>{" "}
                <a className="font-mono text-blue-700 hover:underline" href={previewQuery.data.scanUrl}>
                  {previewQuery.data.scanUrl}
                </a>
              </p>
              <p>
                <span className="font-medium text-slate-700">Redirects to:</span>{" "}
                <span className="font-mono text-slate-900">{previewQuery.data.resolvedUrl}</span>
                <span className="ml-2 text-slate-500">({previewQuery.data.resolution})</span>
              </p>
              {previewQuery.data.context != null && (
                <p className="text-slate-600">
                  SKU {previewQuery.data.context.sku}
                  {previewQuery.data.context.endUserName != null
                    ? ` · ${previewQuery.data.context.endUserName}`
                    : ""}
                  {previewQuery.data.context.salesOrderNumber != null
                    ? ` · SO ${previewQuery.data.context.salesOrderNumber}`
                    : ""}
                </p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitleWithHelp
            hoverHint="Exact SKU rules beat prefix rules. Higher priority wins within the same match kind."
          >
            Destination rules
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="space-y-4">
          {canEdit && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label htmlFor="dest-match-kind">Match kind</Label>
                <select
                  id="dest-match-kind"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={destinationDraft.matchKind}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({
                      ...draft,
                      matchKind: event.target.value as DestinationDraft["matchKind"],
                    }))
                  }
                >
                  <option value="exact_sku">Exact SKU</option>
                  <option value="sku_prefix">SKU prefix</option>
                </select>
              </div>
              <div>
                <Label htmlFor="dest-match-value">Match value</Label>
                <Input
                  id="dest-match-value"
                  value={destinationDraft.matchValue}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({ ...draft, matchValue: event.target.value }))
                  }
                  placeholder="FI-3C-S08BAM or FI-3C"
                />
              </div>
              <div>
                <Label htmlFor="dest-path">Training path or URL</Label>
                <Input
                  id="dest-path"
                  value={destinationDraft.destinationPath}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({ ...draft, destinationPath: event.target.value }))
                  }
                  placeholder="/training/fi-3-series"
                />
              </div>
              <div>
                <Label htmlFor="dest-priority">Priority</Label>
                <Input
                  id="dest-priority"
                  value={destinationDraft.priority}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({ ...draft, priority: event.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="dest-end-user">End user fs account id (optional)</Label>
                <Input
                  id="dest-end-user"
                  value={destinationDraft.endUserFsAccountId}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({ ...draft, endUserFsAccountId: event.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="dest-notes">Notes</Label>
                <Input
                  id="dest-notes"
                  value={destinationDraft.notes}
                  onChange={(event) =>
                    setDestinationDraft((draft) => ({ ...draft, notes: event.target.value }))
                  }
                />
              </div>
            </div>
          )}
          {formError != null && <p className="text-sm text-rose-700">{formError}</p>}
          {canEdit && (
            <HubSaveButton
              dirty={destinationDirty}
              disabled={createDestinationMutation.isPending}
              onClick={() => {
                createDestinationMutation.mutate({
                  sourceCode: selectedSource,
                  matchKind: destinationDraft.matchKind,
                  matchValue: destinationDraft.matchValue.trim(),
                  destinationPath: destinationDraft.destinationPath.trim(),
                  priority: Number(destinationDraft.priority) || 0,
                  endUserFsAccountId:
                    destinationDraft.endUserFsAccountId.trim() === ""
                      ? null
                      : Number(destinationDraft.endUserFsAccountId),
                  notes: destinationDraft.notes.trim() || null,
                });
              }}
            >
              Add destination rule
            </HubSaveButton>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">Match</th>
                  <th className="py-2 pr-4">Destination</th>
                  <th className="py-2 pr-4">Priority</th>
                  <th className="py-2 pr-4">End user</th>
                  <th className="py-2 pr-4">Active</th>
                  {canEdit && <th className="py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {destinations.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-slate-800">
                      {row.matchKind === "sku_prefix" ? "prefix " : ""}
                      {row.matchValue}
                    </td>
                    <td className="py-2 pr-4 font-mono text-slate-700">{row.destinationPath}</td>
                    <td className="py-2 pr-4">{row.priority}</td>
                    <td className="py-2 pr-4">{row.endUserFsAccountId ?? "—"}</td>
                    <td className="py-2 pr-4">{row.isActive ? "Yes" : "No"}</td>
                    {canEdit && (
                      <td className="py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDestinationMutation.mutate(row.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {destinations.length === 0 && (
                  <tr>
                    <td className="py-4 text-slate-500" colSpan={canEdit ? 6 : 5}>
                      No destination rules yet for this source.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sources</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Training site</th>
                  <th className="py-2 pr-4">Fallback</th>
                  <th className="py-2 pr-4">Active</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.code} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono">{source.code}</td>
                    <td className="py-2 pr-4">{source.label}</td>
                    <td className="py-2 pr-4 font-mono text-slate-700">{source.trainingSiteOrigin}</td>
                    <td className="py-2 pr-4 font-mono text-slate-700">{source.fallbackPath}</td>
                    <td className="py-2 pr-4">{source.isActive ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent scans</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Shipment</th>
                  <th className="py-2 pr-4">Batch item</th>
                  <th className="py-2 pr-4">SKU</th>
                  <th className="py-2 pr-4">End user</th>
                  <th className="py-2 pr-4">Resolution</th>
                  <th className="py-2 pr-4">URL</th>
                </tr>
              </thead>
              <tbody>
                {(eventsQuery.data?.events ?? []).map((event) => (
                  <tr key={event.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {new Date(event.scannedAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">{event.shipmentId ?? "—"}</td>
                    <td className="py-2 pr-4">{event.batchItemId ?? "—"}</td>
                    <td className="py-2 pr-4 font-mono">{event.sku ?? "—"}</td>
                    <td className="py-2 pr-4">{event.endUserName ?? "—"}</td>
                    <td className="py-2 pr-4">{event.resolution}</td>
                    <td className="py-2 pr-4 max-w-xs truncate font-mono text-slate-700">
                      {event.resolvedUrl}
                    </td>
                  </tr>
                ))}
                {(eventsQuery.data?.events ?? []).length === 0 && (
                  <tr>
                    <td className="py-4 text-slate-500" colSpan={7}>No scans logged yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
